import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import {
  EVENT_RULES_HEADING,
  overviewEventMenu,
  processEventRulesRow,
  tournamentRulesIntroLines,
} from "../../utility/eventRules";
import { useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";

const MenuRow = ({ item }) => (
  <div className="flex items-baseline justify-between gap-4 py-0.5">
    <p className="min-w-0 text-brand-charcoal">
      {item.name}
      {item.detail ? (
        <span className="text-brand-muted"> {item.detail}</span>
      ) : null}
    </p>
    {item.price ? (
      <span className="font-score tabular-nums text-brand-charcoal flex-shrink-0">
        {item.price}
      </span>
    ) : null}
  </div>
);

const MenuGroup = ({ group }) => {
  const title = group.sharedPrice
    ? `${group.title} (${group.sharedPrice} each)`
    : group.title;

  return (
    <section>
      {title ? (
        <h3 className="heading-display text-base text-brand-charcoal mb-1">
          {title}
        </h3>
      ) : null}
      {group.sharedPrice ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
          {group.items.map((item) => (
            <p key={item.id} className="text-brand-charcoal">
              {item.name}
            </p>
          ))}
        </div>
      ) : (
        group.items.map((item) => <MenuRow key={item.id} item={item} />)
      )}
    </section>
  );
};

const TournamentEvents = ({ tournamentId, tournamentData }) => {
  const tournamentSlug = useTournamentSlug();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const rulesHref = tournamentPath(tournamentSlug, "/rules");
  const introLines = tournamentRulesIntroLines(tournamentData);

  useEffect(() => {
    const fetchEvents = async () => {
      if (!tournamentId) return;

      try {
        setLoading(true);
        setError(null);

        const [
          { data: eventsData, error: eventsError },
          { data: templatesData, error: templatesError },
          { data: bracketsData, error: bracketsError },
        ] = await Promise.all([
          supabase
            .from("tournamentEvents")
            .select(
              `
              id,
              eventName,
              isPrimary,
              bowlerQuantity,
              gamesPerEvent,
              tournamentEventCalculationType (
                id,
                name,
                code
              ),
              eventFee (
                id,
                amount,
                fees (
                  feeName
                )
              )
            `
            )
            .eq("tournamentID", tournamentId)
            .eq("isDeleted", false)
            .is("templateId", null)
            .order("isPrimary", { ascending: false })
            .order("eventName"),
          supabase
            .from("eventSquadTemplate")
            .select(
              `
              id,
              eventName,
              eventSquadTemplateFees (
                id,
                amount,
                fees (
                  feeName
                )
              )
            `
            )
            .eq("tournamentID", tournamentId)
            .eq("isDeleted", false)
            .order("eventName"),
          supabase
            .from("bracketProduct")
            .select("id, name, entryAmount")
            .eq("tournamentID", tournamentId)
            .eq("isDeleted", false)
            .order("createdAt", { ascending: true }),
        ]);

        if (eventsError) throw eventsError;
        if (templatesError) throw templatesError;

        const tournamentEvents = (eventsData || []).map(processEventRulesRow);
        const templates = (templatesData || []).map((template) =>
          processEventRulesRow({
            ...template,
            isPrimary: false,
            bowlerQuantity: 1,
            eventFee: template.eventSquadTemplateFees,
          })
        );
        setGroups(
          overviewEventMenu({
            events: [...tournamentEvents, ...templates],
            brackets: bracketsError ? [] : bracketsData || [],
          })
        );
      } catch (err) {
        console.error("Error fetching events:", err);
        setError("Failed to load event information");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [tournamentId]);

  return (
    <div className="card-board p-6">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="heading-display text-xl">Events</h2>
        <Link
          to={rulesHref}
          className="text-sm font-medium text-brand-gold-ink hover:underline flex-shrink-0"
        >
          {EVENT_RULES_HEADING}
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3 animate-pulse py-2">
          <div className="h-4 bg-brand-charcoal/10 rounded w-1/2" />
          <div className="h-4 bg-brand-charcoal/10 rounded w-2/3" />
          <div className="h-4 bg-brand-charcoal/10 rounded w-1/3" />
        </div>
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : groups.length === 0 ? (
        <p className="text-brand-muted text-sm">
          No events have been added to this tournament yet.
        </p>
      ) : (
        <div className="space-y-4">
          {introLines.length ? (
            <div className="text-sm text-brand-charcoal space-y-1">
              {introLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          ) : null}
          {groups.map((group) => (
            <MenuGroup key={group.key} group={group} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TournamentEvents;
